<template>
  <div>
    <router-view name="cepHeader" :electionsPermissions="electionsPermissions"/>
    <es-loader v-if="loading" style="margin: 6.5rem"/>
    <template v-else>
      <router-view
        name="cepSummary"
        :approvalCertifiedInfo="approvalCertifiedInfo"
        :cepSummary="cepSummary"
        :loadingError="loadingError"
        :loadingSummary="loadingSummary"
        :locationGroupLength="locationGroupLength"
        :selectedLocationGroup="selectedLocationGroup"
        @selectedGroup="selectedLocationGroup = $event"/>
      <router-view name="cepYears" v-if="showCepYears"/>
      <es-exception
        v-if="!loadingError && !locationGroupId && cepTab !=='lealist'"
        exceptionMessage="Please select a location group."/>
      <es-exception
        v-else-if="loadingError"
        exceptionMessage="There was an issue. Please try again or contact your administrator."/>
      <router-view
        name="cepBody"
        :cepSummary="cepSummary"
        :electionsPermissions="electionsPermissions"
        :loadingSummary="loadingSummary"
        :loadingSummaryError="loadingSummaryError"
        @change="onChange"/>
    </template>
    <div class="document-container cep-instructions">
      <router-view name="cepInstructions"/>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import EsException from '@/components/EsException.vue';
import EsLoader from '@/components/EsLoader.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';

export default {
  mixins: [
    BreadcrumbsManager,
  ],
  components: {
    EsException,
    EsLoader,
  },
  props: {
    cepTab: {
      type: String,
    },
    locationGroupId: {
      type: [String, Number],
    },
    schoolYear: {
      type: [String, Number],
    },
  },
  data() {
    return {
      approvalCertifiedInfo: {},
      cepSummary: {},
      electionsSummary: {},
      loading: true,
      loadingError: null,
      loadingSummary: null,
      loadingSummaryError: null,
      locationGroupLength: null,
      populationSummary: {},
      selectedLocationGroup: {},
    };
  },
  watch: {
    updatedProps() {
      if (this.locationGroupId) {
        this.loadLocationGroupSummary();
      } else {
        this.cepSummary = {};
      }
    },
  },
  created() {
    this.onLoad();
    this.setBreadcrumb([
      { text: 'Home', to: { name: 'directMatch' } },
      { text: 'CEP Manager', to: {} },
    ]);
  },
  mounted() {
    this.$root.$on('refreshCepSummary', this.loadLocationGroupSummary);
  },
  computed: {
    updatedProps() {
      return `${this.cepTab}:${this.locationGroupId}:${this.schoolYear}`;
    },
    showCepYears() {
      return !this.loadingError && (this.cepTab === 'lealist' || this.locationGroupId)
        && this.schoolYear && this.cepTab !== 'electionsRecalculate';
    },
    electionsPermissions() {
      // Use Date objects for all date comparisons to avoid bugs from mixing string and Date types
      // Helper to parse date-only strings as local dates to avoid timezone offset issues
      const normalizeDate = (d) => {
        if (!d) return null;
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
          // Parse as local date: new Date(year, monthIndex, day)
          const [year, month, day] = d.split('-').map(Number);
          const date = new Date(year, month - 1, day);
          return date;
        }
        const date = new Date(d);
        if (Number.isNaN(date.getTime())) return null;
        date.setHours(0, 0, 0, 0);
        return date;
      };

      const summary = this.cepSummary || {};
      const today = normalizeDate(new Date());
      const popDue = normalizeDate(summary.populationDueDate);
      const electDue = normalizeDate(summary.electionsDueDate);

      const canAccess = summary.populationStatus === 'A' && popDue && today > popDue;
      const viewOnly = !canAccess || (electDue && today > electDue)
        || ['A', 'S'].includes(summary.electionStatus);
      return {
        canAccess,
        viewOnly,
        submissionReady: summary.submissionReady,
      };
    },
  },
  methods: {
    fetchSelectedGroup() {
      return axios.get(`location-groups/${this.locationGroupId}`);
    },
    async onChange() {
      await this.loadLocationGroupSummary();
    },
    async loadLocationGroupSummary() {
      if (this.cepTab === 'lealist') {
        return;
      }

      try {
        this.loadingSummary = true;
        this.loadingSummaryError = false;
        // Fetch both population and elections summaries and store separately
        const [populationRes, electionsRes] = await Promise.all([
          axios.get(`cep/${this.locationGroupId}/population/${this.schoolYear}/summary`),
          axios.get(`cep/${this.locationGroupId}/elections/${this.schoolYear}/summary`),
        ]);
        this.populationSummary = populationRes.data;
        this.electionsSummary = electionsRes.data;

        // Store all 8 approval/certified fields of population
        // as well as election in a single object
        this.approvalCertifiedInfo = {
          populationApprovalBy: this.populationSummary.approvalBy,
          populationApprovalDate: this.populationSummary.approvalDate,
          populationCertifiedBy: this.populationSummary.certifiedBy,
          populationCertifiedDate: this.populationSummary.certifiedDate,
          electionsApprovalBy: this.electionsSummary.approvalBy,
          electionsApprovalDate: this.electionsSummary.approvalDate,
          electionsCertifiedBy: this.electionsSummary.certifiedBy,
          electionsCertifiedDate: this.electionsSummary.certifiedDate,
        };

        // Assign cepSummary based on tab
        const tab = this.cepTab && this.cepTab.includes('elections') ? 'elections' : this.cepTab;
        this.cepSummary = tab === 'elections' ? this.electionsSummary : this.populationSummary;
      } catch (e) {
        this.loadingSummaryError = true;
        this.cepSummary = {};
      } finally {
        this.loadingSummary = false;
      }
    },
    async onLoad() {
      try {
        this.loading = true;
        this.loadingError = false;

        const res = await axios.get('cep/location-groups?organizations=FNS');
        this.locationGroupLength = res.data.data.length;

        if (!this.locationGroupId) {
          if (this.locationGroupLength === 1) {
            [this.selectedLocationGroup] = res.data.data;
            this.$router.replace({
              path: `${this.$route.path}`,
              query: {
                locationGroupId: res.data.data[0].id,
                schoolYear: this.schoolYear,
              },
            });
          }
        } else {
          const groupResponse = await this.fetchSelectedGroup();
          this.selectedLocationGroup = groupResponse.data;
          this.loadLocationGroupSummary();
        }
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .cep-instructions
    padding-top: 0.25rem
    padding-bottom: 0.25rem
</style>
